<script>
	import _ from 'underscore';
	import assignmentService from 'services/assignmentService';
	const { events } = require('utilities/analyticsUtilities');
	import { studentCompleteOfflineVocabActivity } from 'utilities/offlineUtilities';
	import pubsub from 'events/pubsub';

	const {
		fireEvent,
		events: { VOCAB_ACTIVITY_COMPLETE, VOCAB_ACTIVITY_RESET }
	} = require('utilities/analyticsUtilities');

	export default {
		props: ['wordList', 'wordChanger', 'assignmentId', 'activityState', 'product', 'selectedProduct', 'showTts'],
		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default,
			'vocab-powered-by-wordsmyth': require('components/vocab-powered-by-wordsmyth.vue').default
		},
		modelData: ['offlineMode', 'printing'],
		data() {
			return {
				events,
				selectedWord: null,
				dragging: null,
				draggingOver: null,
				completedWords: {},
				animateCompletion: true,
				shuffledChangerWords: []
			};
		},
		created() {
			this.reset(false);
			if (this.activityState && this.activityState.wc) {
				this.completeNow();
			}
		},
		methods: {
			reset(emitEvent = true) {
				const changer = this.wordChanger;
				const words = Object.keys(changer).map(k => ({
					word: changer[k].occurrence_form,
					key: k,
					originalWord: k
				}));

				this.completedWords = {};
				this.dragging = null;
				this.animateCompletion = true;
				this.shuffledChangerWords = _.shuffle(words);

				if (emitEvent) {
					fireEvent(VOCAB_ACTIVITY_RESET, this.eventData);
				}
			},
			toggleWordSelect(wordKey) {
				if (this.selectedWord === wordKey) {
					this.selectedWord = null;
				} else if (!this.completedWords[wordKey]) {
					this.selectedWord = wordKey;
				}
			},
			selectSentence(wordKey) {
				if (this.selectedWord === wordKey) {
					this.completedWords = _.assign({}, this.completedWords, {
						[wordKey]: true
					});
				}
			},
			startDragging(event, word) {
				event.dataTransfer.setData('text/plain', '');
				this.dragging = word;
			},
			stopDragging() {
				this.dragging = null;
			},
			dragOver(event, wordKey) {
				if (this.dragging === wordKey) {
					event.preventDefault();
				}

				this.draggingOver = wordKey;
			},
			dragOut(event, wordKey) {
				this.draggingOver = null;
			},
			droppedOnto(word) {
				if (this.dragging === word) {
					this.completedWords = _.assign({}, this.completedWords, {
						[word]: true
					});
				}
			},
			completeNow() {
				const changer = this.wordChanger;
				this.animateCompletion = false;
				this.completedWords = Object.keys(changer).reduce((lookup, k) => {
					lookup[k] = true;
					return lookup;
				}, {});
				this.dragging = null;
			}
		},
		computed: {
			complete() {
				return Object.keys(this.completedWords).length === Object.keys(this.wordChanger).length;
			},
			changerSentences() {
				const sentences = [];

				// iterate using word_list to preserve ordering
				this.wordList.forEach(w => {
					if (this.wordChanger[w]) {
						const occurrenceForm = this.wordChanger[w].occurrence_form.toLowerCase();
						const parts = this.wordChanger[w].sentence.split(new RegExp('(' + occurrenceForm + ')', 'i'));
						sentences.push({
							parts,
							key: w,
							completeReading: this.wordChanger[w].sentence,
							readingWithBlanks: parts.map(p => (p.toLowerCase() === occurrenceForm ? '(blank)' : p)).join(''),
							occurrenceForm
						});
					}
				});
				return sentences;
			},
			activeProduct() {
				return this.selectedProduct || this.product;
			},
			eventData() {
				return {
					aid: this.assignmentId,
					type: 'wc'
				};
			}
		},
		watch: {
			completedWords() {
				this.dragging = null;
				this.selectedWord = null;
			},
			complete(c) {
				const data = this.eventData;
				if (c && this.assignmentId) {
					fireEvent(VOCAB_ACTIVITY_COMPLETE, data);
				}
				if (c && this.activityState && this.assignmentId && !this.activityState.wb) {
					let prms = null;
					if (this.offlineMode) {
						prms = studentCompleteOfflineVocabActivity(data);
					} else {
						prms = assignmentService.completeVocabActivity(data);
					}

					prms.then(() => {
						pubsub.publish('vocab-activity-complete', { wc: true });
					});
				}
			}
		}
	};
</script>
<template>
	<div
		class="vocab-changer-activity-container"
		:class="{ printing: printing }">
		<header>
			<h3><span v-if="printing">2.</span> Word Changer</h3>
			<p v-if="printing">
				Words have different forms when we use them in different ways. Write the correct vocabulary word, in its correct form, in each blank.
			</p>
			<p v-else>
				Words have different forms when we use them in different ways. Move the right vocabulary word to each blank. See if and how the word changes to
				fit the sentence!
				<text-to-speech-player-simple
					v-if="showTts"
					text="Words have different forms when we use them in different ways. Move the right vocabulary word to each blank. See if and how the word changes to fit the sentence!"
					:event-data="{
						aid: assignmentId,
						pid: activeProduct.id,
						tag: 'vocab_changer_instructions'
					}">
				</text-to-speech-player-simple>
			</p>
		</header>
		<div
			class="vocab-activity vocab-changer-activity"
			:class="{ completed: complete }">
			<h2 class="completion-header">Congratulations!</h2>
			<button
				v-if="complete"
				class="button -outline button-reset"
				@click="reset">
				Reset
			</button>
			<div
				class="word-bank"
				v-else>
				<div
					v-for="wordRec in shuffledChangerWords"
					@click="toggleWordSelect(wordRec.key)"
					:data-test-word-bank="wordRec.key"
					:key="wordRec.key"
					class="word-wrapper"
					:class="{
						completed: completedWords[wordRec.key],
						selected: selectedWord === wordRec.key
					}">
					<div class="word">
						<div
							draggable="true"
							@dragstart="startDragging($event, wordRec.key)"
							@dragend="stopDragging">
							{{ wordRec.originalWord }}
							<audio-player-simple
								v-if="dragging !== wordRec.key && selectedWord !== wordRec.key"
								@click.native.stop
								no-pause
								:url="product.wordAudioUrl(wordRec.originalWord)"
								:event-data="{
									aid: workspace && workspace.aid,
									pid: activeProduct.id,
									word: wordRec.originalWord
								}"
								:audio-play-event-name="events.VOCAB_CHANGER_AUDIO_PLAY"
								:audio-pause-event-name="events.VOCAB_CHANGER_AUDIO_PAUSE"
								:audio-complete-event-name="events.VOCAB_CHANGER_AUDIO_COMPLETE">
							</audio-player-simple>
						</div>
					</div>
				</div>
			</div>
			<p
				v-for="sentence in changerSentences"
				:key="sentence.key">
				<span
					v-for="(part, partIndex) in sentence.parts"
					:key="partIndex">
					<span
						v-if="part.toLowerCase() === sentence.occurrenceForm"
						class="changer-blank"
						:class="{
							completed: completedWords[sentence.key],
							'complete-animation': completedWords[sentence.key] && animateCompletion,
							'word-ready':
								(!!selectedWord && !completedWords[sentence.key]) || (!!draggingOver && draggingOver === dragging && draggingOver === sentence.key)
						}"
						@dragover="dragOver($event, sentence.key)"
						@dragout="dragOut($event, sentence.key)"
						@drop="droppedOnto(sentence.key)"
						@click="selectSentence(sentence.key)"
						:data-test-sentence="sentence.key">
						<span>{{ part }}</span>
					</span>
					<span v-else>{{ part }}</span>
				</span>
				<text-to-speech-player-simple
					v-if="showTts"
					:text="completedWords[sentence.key] ? sentence.completeReading : sentence.readingWithBlanks"
					:event-data="{
						aid: assignmentId,
						pid: activeProduct.id,
						tag: completedWords[sentence.key] ? 'vocab_changer_completed_sentence' : 'vocab_changer_incomplete_sentence',
						word: sentence.key
					}">
				</text-to-speech-player-simple>
			</p>
			<vocab-powered-by-wordsmyth></vocab-powered-by-wordsmyth>
		</div>
	</div>
</template>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.vocab-changer-activity {
		.completion-header {
			text-align: center;
		}

		.word-bank {
			.word-wrapper {
				display: inline-block;
				width: 50%;

				@media only screen and (min-width: $bp-width-med) {
					width: 12rem;
				}

				height: 4rem;
				position: relative;

				.word {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translateX(-50%) translateY(-50%);
					font-weight: bold;
					color: $c-blue-dark;

					> div {
						padding: 0 0.1rem;
						position: relative;
						white-space: nowrap;
					}
				}

				&.completed .word {
					color: $c-slate-dark;
					opacity: 0.5;
				}

				&.selected .word > div {
					background-color: $c-blue-dark;
					color: white;
				}
			}
		}

		.changer-blank {
			margin: 0 0.2rem 0 0.1rem;
			border-bottom: 2px solid $c-blue-dark;
			transform-origin: center;
			display: inline-block;

			> span {
				visibility: hidden;
			}

			&.word-ready {
				border: 2px dashed $c-blue-dark;
				transform-origin: center;
				animation-name: pulsing;
				animation-duration: 1.5s;
				animation-iteration-count: infinite;
			}

			&.completed {
				> span {
					visibility: visible;
				}
			}

			&.complete-animation {
				animation-name: changer_word_completed;
				animation-duration: 0.7s;
			}
		}

		&.completed {
			.changer-blank {
				border-bottom: none;
				color: $c-blue-dark;
				font-weight: bold;
			}
		}

		@keyframes pulsing {
			0% {
				transform: scale(1);
			}

			50% {
				transform: scale(1.07);
			}

			100% {
				transform: scale(1);
			}
		}

		@keyframes changer_word_completed {
			0% {
				transform: scale(1);
			}

			20% {
				transform: scale(0.6);
			}

			100% {
				transform: scale(1);
			}
		}
	}

	.vocab-changer-activity-container {
		&.printing {
			.vocab-activity {
				font-size: 1rem;
			}
			.word-bank {
				display: none;
			}
			.changer-blank {
				width: 10rem;
			}
		}
	}
</style>
